/**
 * Чистые функции экрана «Выберите вариант» (RP-FB66): процент шагом 5 и разметка отличий в названии. Ничего не пересчитывают по существу:
 * порядок, отличия и серии приходят от сервера.
 */

/**
 * Процент для показа шагом 5, округление вниз («82 %» → «80 %»): два соседних варианта 82 % и 83 % видны равными, и это сигнал «выберите
 * вариант». 100 — только когда сервер дал полное совпадение (1); иначе потолок 95. Не число — `null`.
 */
export function stepPercent(confidence: number): number | null {
  if (!Number.isFinite(confidence)) return null
  if (confidence >= 1) return 100
  // Сервер отдаёт шаг 0,01: сначала целые проценты (без хвостов дробей вроде 56,99999), затем вниз к 5.
  const whole = Math.min(99, Math.max(0, Math.round(confidence * 100)))
  return Math.floor(whole / 5) * 5
}

export interface NameSegment {
  text: string
  different: boolean
}

/**
 * Слово или число названия в том же разборе, что и на сервере (`NameDifferences`): буквы подряд либо число с дробной частью. Так отличие
 * «2» совпадает только с числом 2, а не с частью «250».
 */
const NAME_TOKEN = /\p{L}+|\d+(?:[.,]\d+)?/gu

/** Слово в виде, в котором сервер называет отличие: нижний регистр, десятичная запятая — точкой. */
const tokenKey = (token: string): string => token.replace(',', '.').toLowerCase()

/**
 * Название, разрезанное на куски «обычный» / «отличие». Это разметка уже готового списка отличий от сервера, а не поиск: название разбирается
 * на слова и числа, и выделяются те, что названы в `differences`. Своих сравнений названий нет — отличия берутся только из ответа.
 */
export function highlightSegments(name: string, differences: readonly string[]): NameSegment[] {
  const named = new Set(differences.map((difference) => tokenKey(difference.trim())).filter((key) => key !== ''))
  const segments: NameSegment[] = []
  let cursor = 0
  for (const match of name.matchAll(NAME_TOKEN)) {
    if (!named.has(tokenKey(match[0]))) continue
    const start = match.index
    if (start > cursor) segments.push({ text: name.slice(cursor, start), different: false })
    segments.push({ text: match[0], different: true })
    cursor = start + match[0].length
  }
  if (cursor < name.length) segments.push({ text: name.slice(cursor), different: false })
  return segments
}
